import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs'
import { TagMatchMode } from '../../../../../_generated/graphql'
import { ButtonComponent } from '../../../../components/button/button.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../../components/message-dialog/message-dialog.service'
import {
  Item,
  MultipleSelectComponent,
} from '../../../../components/multiple-select/multiple-select.component'
import {
  Option,
  SelectComponent,
} from '../../../../components/select/select.component'
import {
  AssessmentTagMaster,
  SkillMap,
} from '../../../../services/assessment/assessment.mapping'
import { AssessmentService } from '../../../../services/assessment/assesssment.service'
import {
  RoleModelSelectionSetting,
  UserService,
} from '../../../../services/user/user.service'
import { captureException } from '../../../../util/captureException'
import {
  buildRoleModelSelectionRows,
  buildRoleModelTagItems,
  isActiveTag,
  RoleModelSelectionListRow,
} from './role-model-recommendation-setting.logic'

interface SelectionForm {
  mode: 'add' | 'edit'
  skillMap: Option
  selectedTagMasterIds: string[]
  matchMode: TagMatchMode
  items: Item[]
}

@UntilDestroy()
@Component({
  selector: 'app-role-model-recommendation-setting',
  standalone: true,
  imports: [
    MatCheckboxModule,
    ButtonComponent,
    SelectComponent,
    MultipleSelectComponent,
  ],
  templateUrl: './role-model-recommendation-setting.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './role-model-recommendation-setting.component.scss',
})
export class RoleModelRecommendationSettingComponent implements OnInit {
  useRoleModelRecommendation = false
  recommendationLoading = true
  recommendationLoadFailed = false
  recommendationSaving = false
  roleModelSelectionLoading = false
  roleModelSelectionLoadFailed = false
  roleModelSelectionSaving = false
  confirmingDelete = false
  roleModelSelectionRows: RoleModelSelectionListRow[] = []
  availableSkillMaps: Option[] = []
  form: SelectionForm | null = null
  readonly tagMatchOptions = [
    { value: TagMatchMode.Any, label: 'いずれかを含む' },
    { value: TagMatchMode.All, label: 'すべてを含む' },
  ]
  readonly TagMatchMode = TagMatchMode
  tagMasters: AssessmentTagMaster[] = []
  private skillMaps: SkillMap[] = []
  private settings: RoleModelSelectionSetting[] = []

  constructor(
    private userService: UserService,
    private assessmentService: AssessmentService,
    private messageDialogService: MessageDialogService,
    private snackBar: MatSnackBar,
  ) {}

  get busy(): boolean {
    return (
      this.recommendationLoading ||
      this.recommendationSaving ||
      this.roleModelSelectionLoading ||
      this.roleModelSelectionSaving ||
      this.confirmingDelete
    )
  }

  get showSelectionSection(): boolean {
    return (
      this.recommendationLoading ||
      this.roleModelSelectionLoading ||
      this.roleModelSelectionLoadFailed ||
      this.tagMasters.length > 0 ||
      this.settings.length > 0
    )
  }

  get selectionDisabled(): boolean {
    return (
      !this.useRoleModelRecommendation ||
      this.busy ||
      this.roleModelSelectionLoadFailed
    )
  }

  get hasInvalidTags(): boolean {
    return (
      this.form != null &&
      this.form.selectedTagMasterIds.some(
        (id) => !isActiveTag(this.tagMasters, id),
      )
    )
  }

  get canSave(): boolean {
    const form = this.form
    return (
      !this.selectionDisabled &&
      form != null &&
      form.skillMap.id !== '' &&
      (form.mode === 'edit' ||
        this.availableSkillMaps.some((map) => map.id === form.skillMap.id)) &&
      form.selectedTagMasterIds.length > 0 &&
      !this.hasInvalidTags
    )
  }

  ngOnInit(): void {
    this.userService
      .getAdminOrg()
      .pipe(
        tap((org) => {
          this.useRoleModelRecommendation = org.useRoleModelRecommendation
          this.recommendationLoading = false
        }),
        switchMap(() => {
          this.roleModelSelectionLoading = true
          return forkJoin({
            skillMaps: this.assessmentService.getSkillMaps(),
            tagMasters: this.assessmentService.getAssessmentTagMasters(),
            settings: this.userService.getRoleModelSelectionSettings(),
          }).pipe(
            tap(({ skillMaps, tagMasters, settings }) => {
              this.skillMaps = skillMaps
              this.tagMasters = tagMasters
              this.refreshSettings(settings)
            }),
          )
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.recommendationLoadFailed = this.recommendationLoading
          this.roleModelSelectionLoadFailed = true
          return of()
        }),
        finalize(() => {
          this.recommendationLoading = false
          this.roleModelSelectionLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  onUseRoleModelRecommendationChange(event: MatCheckboxChange): void {
    if (this.busy || this.recommendationLoadFailed) return
    const previous = this.useRoleModelRecommendation
    this.useRoleModelRecommendation = event.checked
    this.recommendationSaving = true
    this.userService
      .saveUseRoleModelRecommendation(event.checked)
      .pipe(
        tap(() =>
          this.snackBar.open(
            'ハイパフォーマー商談レコメンド設定を更新しました。',
            'close',
            { duration: 3000 },
          ),
        ),
        catchError((error: unknown) => {
          captureException(error)
          event.source.checked = previous
          this.useRoleModelRecommendation = previous
          this.messageDialogService.showError(
            'ハイパフォーマー商談レコメンド設定の更新に失敗しました。時間を置いて再度お試しください。',
            { title: 'エラー' },
          )
          return of()
        }),
        finalize(() => {
          this.recommendationSaving = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  addCondition(): void {
    if (
      this.form != null ||
      this.selectionDisabled ||
      this.availableSkillMaps.length === 0 ||
      this.tagMasters.length === 0
    )
      return
    this.form = {
      mode: 'add',
      skillMap: { id: '', name: 'スキルマップを選択' },
      selectedTagMasterIds: [],
      matchMode: TagMatchMode.Any,
      items: buildRoleModelTagItems(this.tagMasters, []),
    }
  }

  editCondition(row: RoleModelSelectionListRow): void {
    if (this.form != null || this.selectionDisabled || !row.canEdit) return
    const setting = this.settings.find(
      (setting) => setting.skillMapId === row.skillMapId,
    )
    if (setting == null) throw new Error('選出条件がありません')
    this.form = {
      mode: 'edit',
      skillMap: { id: row.skillMapId, name: row.skillMapName },
      selectedTagMasterIds: [...setting.tagMasterIds],
      matchMode: setting.tagMatch,
      items: buildRoleModelTagItems(this.tagMasters, setting.tagMasterIds),
    }
  }

  onSkillMapChange(option: Option): void {
    if (this.selectionDisabled || this.form == null || this.form.mode !== 'add')
      return
    const skillMap = this.availableSkillMaps.find((map) => map.id === option.id)
    if (skillMap == null)
      throw new Error('追加可能なスキルマップではありません')
    this.form.skillMap = skillMap
  }

  onRoleModelTagsChange(items: Item[]): void {
    if (
      this.selectionDisabled ||
      this.form == null ||
      this.form.skillMap.id === ''
    )
      return
    const invalidIds = this.form.selectedTagMasterIds.filter(
      (id) => !isActiveTag(this.tagMasters, id),
    )
    const selectedIds = items
      .filter((item) => item.checked)
      .map((item) => {
        if (item.id == null) throw new Error('タグ ID がありません')
        return item.id
      })
    this.form.selectedTagMasterIds = [
      ...new Set([...selectedIds, ...invalidIds]),
    ]
    this.form.items = buildRoleModelTagItems(
      this.tagMasters,
      this.form.selectedTagMasterIds,
    )
  }

  onRoleModelMatchModeChange(mode: string): void {
    if (
      this.selectionDisabled ||
      this.form == null ||
      this.form.skillMap.id === ''
    )
      return
    if (mode !== TagMatchMode.Any && mode !== TagMatchMode.All)
      throw new Error('不正なタグ一致条件です')
    this.form.matchMode = mode
  }

  removeInvalidRoleModelTags(): void {
    if (this.selectionDisabled || this.form == null) return
    this.form.selectedTagMasterIds = this.form.selectedTagMasterIds.filter(
      (id) => isActiveTag(this.tagMasters, id),
    )
    this.form.items = buildRoleModelTagItems(
      this.tagMasters,
      this.form.selectedTagMasterIds,
    )
  }

  cancelEditing(): void {
    if (this.selectionDisabled) return
    this.form = null
  }

  saveRoleModelSelectionSetting(): void {
    if (!this.canSave || this.form == null) return
    this.updateRoleModelSelectionSetting(
      this.userService.upsertRoleModelSelectionSetting(
        this.form.skillMap.id,
        [...this.form.selectedTagMasterIds],
        this.form.matchMode,
      ),
      '保存',
      true,
    )
  }

  deleteRoleModelSelectionSetting(row: RoleModelSelectionListRow): void {
    if (this.selectionDisabled) return
    this.confirmingDelete = true
    this.messageDialogService
      .showConfirm(
        `${row.skillMapName} の選出条件を解除します。よろしいですか？`,
        {
          primaryButtonText: '解除',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        finalize(() => {
          this.confirmingDelete = false
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        this.confirmingDelete = false
        if (result !== DialogResult.PrimaryButtonClicked) return
        if (this.selectionDisabled) return
        this.updateRoleModelSelectionSetting(
          this.userService.deleteRoleModelSelectionSetting(row.skillMapId),
          '解除',
          this.form?.skillMap.id === row.skillMapId,
        )
      })
  }

  private updateRoleModelSelectionSetting(
    operation: Observable<void>,
    action: '保存' | '解除',
    closeFormOnSuccess: boolean,
  ): void {
    this.roleModelSelectionSaving = true
    operation
      .pipe(
        switchMap(() =>
          this.userService.getRoleModelSelectionSettings().pipe(
            tap((settings) => {
              this.refreshSettings(settings)
              if (closeFormOnSuccess) this.form = null
              this.snackBar.open(
                `ロールモデル商談の選出条件を${action}しました`,
                'close',
                { duration: 3000 },
              )
            }),
            catchError((error: unknown) => {
              captureException(error)
              this.roleModelSelectionLoadFailed = true
              this.messageDialogService.showError(
                `ロールモデル商談の選出条件を${action}しましたが、最新の設定を取得できませんでした。画面を再読み込みしてください。`,
                { title: 'エラー' },
              )
              return of()
            }),
          ),
        ),
        catchError((error: unknown) => {
          captureException(error)
          this.messageDialogService.showError(
            `ロールモデル商談の選出条件の${action}に失敗しました。時間を置いて再度お試しください。`,
            { title: 'エラー' },
          )
          const previousInvalidIds =
            this.form == null
              ? []
              : this.form.selectedTagMasterIds.filter(
                  (id) => !isActiveTag(this.tagMasters, id),
                )
          return forkJoin({
            tagMasters: this.assessmentService.getAssessmentTagMasters(),
            settings: this.userService.getRoleModelSelectionSettings(),
          }).pipe(
            tap(({ tagMasters, settings }) => {
              this.tagMasters = tagMasters
              this.refreshSettings(settings)
              if (tagMasters.length === 0) this.form = null
              if (this.form != null) {
                this.form.items = buildRoleModelTagItems(
                  tagMasters,
                  this.form.selectedTagMasterIds,
                )
                if (
                  this.form.selectedTagMasterIds.some(
                    (id) =>
                      !isActiveTag(tagMasters, id) &&
                      !previousInvalidIds.includes(id),
                  )
                ) {
                  this.snackBar.open(
                    '無効化されたタグがあります。外してから保存してください',
                    'close',
                    { duration: 3000 },
                  )
                }
              }
            }),
            catchError((refreshError: unknown) => {
              captureException(refreshError)
              return of()
            }),
          )
        }),
        finalize(() => {
          this.roleModelSelectionSaving = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private refreshSettings(settings: RoleModelSelectionSetting[]): void {
    this.settings = settings
    this.roleModelSelectionRows = buildRoleModelSelectionRows(
      this.skillMaps,
      this.tagMasters,
      settings,
    )
    this.availableSkillMaps = this.skillMaps
      .filter(
        (map) => !settings.some((setting) => setting.skillMapId === map.id),
      )
      .map(({ id, name }) => ({ id, name }))
  }
}
